import React, { createContext, useCallback, useContext, useState } from 'react';
import {
  Answer,
  getAnswersBySectionId,
} from 'donor/portal/pages/FeedbackDashboard/Feedback/api';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import { removeConnections, useClient } from 'shared/apollo';
import { FeedbackSectionId } from 'donor/portal/pages/FeedbackDashboard/Feedback/utils';
import { requestInit, RequestState } from 'shared/auth/hooks/useRequestState';
import { TrueImpactChild } from 'shared/design-system/components/TrueImpactBadge';

export const AnswersBySectionContext = createContext<{
  answers: {
    [reportId: number]: {
      [id: number]: Answer;
    };
  };
  requestAnswerState: RequestState<{
    [id: number]: Answer;
  }>;
  handleSection: (reportId: number, sectionId: FeedbackSectionId) => void;
}>({
  answers: {},
  handleSection: () => null,
  requestAnswerState: requestInit(),
});

export const useAnswersBySectionContext = () =>
  useContext(AnswersBySectionContext);

const AnswersBySectionProvider: React.FC<{ children: TrueImpactChild }> = ({
  children,
}) => {
  const client = useClient();
  const [answers, setAnswers] = useState<{
    [reportId: number]: {
      [sectionId in FeedbackSectionId]: {
        [id: number]: Answer;
      };
    };
  }>({});
  const [requestState, makeRequest] = useGraphQLRequest<{
    [id: number]: Answer;
  }>();

  const handleSection = useCallback(
    (reportId: number, sectionId: FeedbackSectionId, force?: boolean) => {
      if (!client) return;
      if (!force && answers[reportId]?.[sectionId]) {
        return;
      }
      const request = getAnswersBySectionId(client, reportId, sectionId).then(
        r => {
          const result: { [id: number]: Answer } = {};
          const answers: Answer[] = removeConnections(
            r.data.allNpoReportAnswers,
          );
          for (const a of answers) {
            result[a.question.dbId] = a;
          }
          return {
            data: result,
          };
        },
      );
      makeRequest(request);
      request.then(r => {
        setAnswers(prev => ({
          ...prev,
          [reportId]: {
            ...prev[reportId],
            [sectionId]: prev[reportId]?.[sectionId]
              ? { ...prev[reportId][sectionId], ...r.data }
              : { ...r.data },
          },
        }));
      });
    },
    [client, answers, setAnswers, makeRequest],
  );

  return (
    <AnswersBySectionContext.Provider
      value={{
        answers,
        handleSection,
        requestAnswerState: requestState,
      }}
    >
      {children}
    </AnswersBySectionContext.Provider>
  );
};

export default AnswersBySectionProvider;
